/**
 * DataPrepStepCard Component
 * 
 * Displays a single data prep step as a card with:
 * - Operation icon and name
 * - Target column(s)
 * - Enable/Disable toggle
 * - Edit/Delete buttons
 * - Drag handle for reordering
 * - Inline warnings/stats
 */

import React from 'react';
import {
    AlertCircle, Copy, Type, RefreshCw, TrendingUp, Calculator, Columns,
    GripVertical, Trash2, Edit2, ToggleLeft, ToggleRight, AlertTriangle, Info
} from 'lucide-react';
import type { DataPrepStepWithMeta, StepResult, DataPrepStepType, DATA_PREP_STEP_DISPLAYS } from '../../services/dataPrepTypes';

interface DataPrepStepCardProps {
    stepWithMeta: DataPrepStepWithMeta;
    stepResult?: StepResult;
    onToggle: (id: string, enabled: boolean) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    isDragging?: boolean;
    dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

const stepIcons: Record<DataPrepStepType, React.FC<{ className?: string }>> = {
    'null-handling': AlertCircle,
    'duplicate-handling': Copy,
    'string-cleaning': Type,
    'type-casting': RefreshCw,
    'normalization': TrendingUp,
    'calculated-column': Calculator,
    'column-management': Columns
};

const stepLabels: Record<DataPrepStepType, string> = {
    'null-handling': 'Null Handling',
    'duplicate-handling': 'Remove Duplicates',
    'string-cleaning': 'String Cleaning',
    'type-casting': 'Type Casting',
    'normalization': 'Normalization',
    'calculated-column': 'Calculated Column',
    'column-management': 'Column Management'
};

function getStepDescription(step: DataPrepStepWithMeta['step']): string {
    switch (step.type) {
        case 'null-handling':
            return step.action === 'remove-rows'
                ? `Remove rows with null in "${step.column}"`
                : `Replace null in "${step.column}" with ${step.replacement === 'custom' ? `"${step.customValue}"` : step.replacement}`;
        case 'duplicate-handling':
            return step.keyColumns.length > 0
                ? `Key: ${step.keyColumns.join(', ')}`
                : 'Based on all columns';
        case 'string-cleaning':
            const ops = step.operations.join(', ');
            return `"${step.column}": ${ops}${step.findReplace ? ` + replace` : ''}`;
        case 'type-casting':
            return `"${step.column}" → ${step.targetType}${step.removeInvalid ? ' (remove invalid)' : ''}`;
        case 'normalization':
            return `${step.columns.join(', ')} → ${step.method}${step.createNewColumn ? ' (new column)' : ''}`;
        case 'calculated-column':
            return `${step.newColumnName} = ${step.formula}`;
        case 'column-management':
            switch (step.action) {
                case 'rename': return `Rename "${step.column}" → "${step.newName}"`;
                case 'hide': return `Hide "${step.column}"`;
                case 'delete': return `Delete "${step.column}"`;
                case 'reorder': return `Move "${step.column}" to position ${step.newPosition}`;
                default: return step.column;
            }
        default:
            return '';
    }
}

export const DataPrepStepCard: React.FC<DataPrepStepCardProps> = ({
    stepWithMeta,
    stepResult,
    onToggle,
    onEdit,
    onDelete,
    isDragging,
    dragHandleProps
}) => {
    const { id, step, enabled } = stepWithMeta;
    const Icon = stepIcons[step.type];
    const label = stepLabels[step.type];
    const description = getStepDescription(step);

    const hasWarnings = stepResult?.warnings && stepResult.warnings.length > 0;
    const hasErrors = stepResult?.warnings?.some(w => w.severity === 'error');

    return (
        <div
            className={`
                relative rounded-lg border transition-all duration-200
                ${isDragging ? 'shadow-lg scale-[1.02] z-10' : ''}
                ${enabled
                    ? 'bg-white/5 border-white/20 hover:border-white/30'
                    : 'bg-white/[0.02] border-white/10 opacity-60'
                }
                ${hasErrors ? 'border-red-500/50' : ''}
            `}
        >
            <div className="flex items-center gap-3 p-3">
                {/* Drag Handle */}
                <div
                    {...dragHandleProps}
                    className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-400 touch-none"
                >
                    <GripVertical className="w-4 h-4" />
                </div>

                {/* Icon */}
                <div className={`p-2 rounded-lg ${enabled ? 'bg-neon-gold/10 text-neon-gold' : 'bg-white/5 text-gray-500'}`}>
                    <Icon className="w-4 h-4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${enabled ? 'text-gray-200' : 'text-gray-500'}`}>
                            {label}
                        </span>
                        {hasWarnings && !hasErrors && (
                            <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
                        )}
                        {hasErrors && (
                            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                        )}
                    </div>
                    <p className={`text-xs truncate ${enabled ? 'text-gray-400' : 'text-gray-600'}`}>
                        {description}
                    </p>
                </div>

                {/* Stats */}
                {stepResult && enabled && (
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
                        {stepResult.stats.rowsRemoved > 0 && (
                            <span className="px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded">
                                -{stepResult.stats.rowsRemoved} rows
                            </span>
                        )}
                        {stepResult.stats.columnsAdded > 0 && (
                            <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded">
                                +{stepResult.stats.columnsAdded} col
                            </span>
                        )}
                        <span className="text-gray-600">
                            {stepResult.stats.executionTimeMs.toFixed(0)}ms
                        </span>
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onEdit(id)}
                        className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-white/5 rounded transition-colors"
                        title="Edit step"
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onDelete(id)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete step"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onToggle(id, !enabled)}
                        className={`p-1.5 rounded transition-colors ${enabled
                                ? 'text-neon-gold hover:bg-neon-gold/10'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                            }`}
                        title={enabled ? 'Disable step' : 'Enable step'}
                    >
                        {enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Warnings/Errors */}
            {hasWarnings && enabled && (
                <div className="px-3 pb-2 pt-0">
                    <div className="flex flex-wrap gap-1">
                        {stepResult!.warnings.slice(0, 3).map((warning, idx) => (
                            <span
                                key={idx}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${warning.severity === 'error'
                                        ? 'bg-red-500/10 text-red-400'
                                        : warning.severity === 'warning'
                                            ? 'bg-yellow-500/10 text-yellow-400'
                                            : 'bg-blue-500/10 text-blue-400'
                                    }`}
                            >
                                {warning.severity === 'error' ? <AlertCircle className="w-3 h-3" /> :
                                    warning.severity === 'warning' ? <AlertTriangle className="w-3 h-3" /> :
                                        <Info className="w-3 h-3" />}
                                {warning.message}
                            </span>
                        ))}
                        {stepResult!.warnings.length > 3 && (
                            <span className="text-xs text-gray-500">
                                +{stepResult!.warnings.length - 3} more
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataPrepStepCard;
